import React, { useCallback, useEffect, useRef, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import {
  getDealerAdminInspection,
  getDealerAdminInquiry,
  replyToDealerAdminInquiry,
  updateDealerAdminInspection,
  chargeDealerAdminInspectionFee,
  uploadDealerInspectionMedia,
  removeDealerInspectionMedia,
  openDealerGuestAccount,
  formatUsd,
} from "../../../services/dealerGarage";
import DepositRequestBox from "./DepositRequestBox";
import DealerWaitBox from "./DealerWaitBox";
import GuestAccountBox from "./GuestAccountBox";
import StaffNotesBox from "./StaffNotesBox";
import CustomerLink from "./CustomerLink";
import PanelFrame from "./PanelFrame";
import MoreMenu from "./MoreMenu";
import { StepProgress, VehicleThumb } from "./PurchasePanel";
import InquiryThread from "../../GarageContent/InquiryThread";
import {
  formatDate, formatPhone, vehicleLabel, customerName, customerIdOf, customerRefOf, errorMessage, todayYmd,
  ageLabel, hoursSince, daysUntil, inspectionAttention, REPLY_ALERT_HOURS,
} from "./dealerAdmin";

// Same as dealerPricing.INSPECTION_FEE_PRESETS: $150 for most, more when the
// inspector travels.
const FEE_PRESETS = [150, 200, 250];

const STATUS_TONE = {
  Requested: styles.chipWarn,
  Accepted: styles.chip,
  Scheduled: styles.chip,
  Completed: styles.chipGood,
  Cancelled: styles.chipMuted,
};

// The strip under the header. A completed inspection sits on Report until
// they decide.
const STAGES = ["Requested", "Accepted", "Booked", "Report", "Decision"];
const stageOf = (i) => ({
  Requested: "Requested",
  Accepted: "Accepted",
  Scheduled: "Booked",
  Completed: i.decision && i.decision.value ? "Decision" : "Report",
}[i.status]);

// Scrolls a form field into view and puts the cursor in it: the header's
// next-step button and More use it to take staff to the right form.
const focusField = (ref) => {
  // After the render that shows it.
  setTimeout(() => {
    if (!ref.current) return;
    ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    ref.current.focus({ preventScroll: true });
  }, 0);
};

/**
 * One inspection: staff notes, accept the request (once the customer's deposit
 * is set up) or turn it down, book it, answer what the customer asked us to check, write
 * the report and upload the photos and video, mark it done (which emails the
 * customer to come and decide), record their answer, and bill the fee.
 *
 * The header holds the car, who it's for, the status, a strip of the five
 * stages and one button for the next move (it takes staff to that stage's
 * form, or starts the sale); under it, the one thing that needs doing now. The
 * current stage's form leads the main column, then what to check and the
 * conversation; the car, deposit, the wait on the Japanese dealer, staff notes,
 * the fee, the account and the history sit beside it.
 *
 * Props: inspectionId, onClose(), onChanged(inspection),
 *        onRecordPurchase(inspection, inquiry) - starts the sale; the inquiry
 *        is the request's lead when it has one, for its quote,
 *        onOpenCustomer(ref) - the customer's name opens their page
 */
const InspectionPanel = ({ inspectionId, onClose, onChanged, onRecordPurchase, onOpenCustomer }) => {
  const [inspection, setInspection] = useState(null);
  const [report, setReport] = useState({ summary: "", grade: "" });
  const [schedule, setSchedule] = useState({ scheduledFor: "", statusNote: "", notify: true });
  const [decision, setDecision] = useState({ value: "", reason: "" });
  const [accept, setAccept] = useState({ statusNote: "", notify: true });
  // The checklist as edited: existing items carry _id; staff-added ones don't yet.
  const [checks, setChecks] = useState([]);
  const [newCheck, setNewCheck] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  // The conversation on the request's inquiry, read and answered from here.
  const [thread, setThread] = useState(null);
  const [showThread, setShowThread] = useState(false);
  const [replying, setReplying] = useState(false);
  const threadRef = useRef(null);
  const [otherFee, setOtherFee] = useState("");
  // Staff notes, as typed. Filled once on the first load and saved on their
  // own, so saving another section (which reloads the form) never clears them.
  const [notes, setNotes] = useState(null);
  // A booked inspection's date form, opened with "Change the date"; the
  // decision form before the inspection is done, opened from More.
  const [changingDate, setChangingDate] = useState(false);
  const [deciding, setDeciding] = useState(false);
  const acceptField = useRef(null);
  const dateField = useRef(null);
  const summaryField = useRef(null);
  const decisionField = useRef(null);

  const apply = useCallback((i) => {
    setInspection(i);
    setReport({ summary: (i.report && i.report.summary) || "", grade: (i.report && i.report.grade) || "" });
    setSchedule({ scheduledFor: i.scheduledFor || "", statusNote: "", notify: true });
    setDecision({ value: (i.decision && i.decision.value) || "", reason: (i.decision && i.decision.reason) || "" });
    setAccept({ statusNote: "", notify: true });
    setChecks((i.checks || []).map((c) => ({ _id: c._id, text: c.text, by: c.by, finding: c.finding || "" })));
    setNewCheck("");
  }, []);

  const load = useCallback(() => {
    getDealerAdminInspection(inspectionId)
      .then((res) => {
        if (res?.message === "Success") {
          apply(res.data);
          setNotes((n) => (n === null ? res.data.adminNotes || "" : n));
        } else {
          alert(errorMessage(res, "Could not load the inspection."));
        }
      })
      .catch(() => alert("Could not load the inspection."));
  }, [inspectionId, apply]);

  useEffect(() => { load(); }, [load]);

  // Opening the thread is the staff read receipt (clears its unread count).
  const inquiryId = inspection && inspection.inquiryId;
  useEffect(() => {
    if (!inquiryId) return;
    getDealerAdminInquiry(inquiryId)
      .then((res) => {
        if (res?.message === "Success" && res.data) {
          setThread(res.data);
          const messages = res.data.messages || [];
          // Open it straight away when the customer is waiting on us.
          if (messages.length && messages[messages.length - 1].author === "customer") setShowThread(true);
        }
      })
      .catch(() => {});
  }, [inquiryId]);

  const sendReply = async (body, files) => {
    setReplying(true);
    try {
      const res = await replyToDealerAdminInquiry(inquiryId, body, files);
      if (res?.message === "Reply sent" && res.data) {
        setThread((t) => ({ ...t, ...res.data }));
        if (onChanged) onChanged(inspection);
        return true;
      }
      alert(errorMessage(res, "Could not send the reply."));
      return false;
    } catch (err) {
      alert("Could not send the reply.");
      return false;
    } finally {
      setReplying(false);
    }
  };

  const update = async (body, label) => {
    setBusy(true);
    try {
      const res = await updateDealerAdminInspection(inspectionId, body);
      if (res?.message === "Inspection updated") {
        apply(res.data);
        if (onChanged) onChanged(res.data);
        return true;
      }
      alert(errorMessage(res, `Could not save the ${label}.`));
      return false;
    } catch (err) {
      alert(`Could not save the ${label}.`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveSchedule = (e) => {
    e.preventDefault();
    if (!schedule.scheduledFor) {
      alert("Pick the inspection date.");
      return;
    }
    const body = { scheduledFor: schedule.scheduledFor };
    // First booking moves it to Scheduled (and emails the customer the date);
    // after that this only changes the date.
    if (inspection.status === "Accepted") {
      Object.assign(body, { status: "Scheduled", statusNote: schedule.statusNote.trim(), notify: schedule.notify });
    }
    update(body, "booking").then((ok) => { if (ok) setChangingDate(false); });
  };

  // Only the notes are taken from the response: unsaved edits in the other
  // sections stay as they are.
  const saveNotes = async () => {
    setBusy(true);
    const res = await updateDealerAdminInspection(inspectionId, { adminNotes: notes }).catch(() => null);
    setBusy(false);
    if (res?.message === "Inspection updated") {
      setInspection((i) => ({ ...i, adminNotes: res.data.adminNotes }));
      if (onChanged) onChanged(res.data);
    } else {
      alert(errorMessage(res, "Could not save the notes."));
    }
  };

  const saveReport = () => update({ report: { summary: report.summary, grade: report.grade.trim() } }, "report");

  const complete = async () => {
    if (!report.summary.trim() && !(inspection.images || []).length) {
      alert("Write the summary or upload photos first.");
      return;
    }
    if (!window.confirm(inspection.accountIsGuest
      ? "Mark the inspection completed? It's a guest account, so no email goes to them - tell them the report is ready yourself."
      : "Mark the inspection completed? The customer is emailed that their report is ready and asked to decide.")) return;
    update({ status: "Completed", statusDate: todayYmd(), report: { summary: report.summary, grade: report.grade.trim() }, notify: true }, "inspection");
  };

  // The fee is never invoiced on its own: it goes on the final car invoice
  // when they buy, and comes off the deposit refund when they don't.
  const setFee = (amount) => update({ fee: amount }, "fee");
  const saveOtherFee = (e) => {
    e.preventDefault();
    const amount = Number(String(otherFee).replace(/[$,\s]/g, ""));
    if (!Number.isFinite(amount) || amount < 0) {
      alert("The fee must be a dollar amount.");
      return;
    }
    setFee(amount).then((ok) => { if (ok) setOtherFee(""); });
  };

  const chargeFee = async () => {
    if (!window.confirm(`Take the ${formatUsd(inspection.feeAmount)} inspection fee from their deposit? Do this when they didn't buy, before refunding the rest.`)) return;
    setBusy(true);
    try {
      const res = await chargeDealerAdminInspectionFee(inspectionId);
      if (res?.message === "Inspection fee taken from the deposit" && res.data) {
        apply(res.data);
        if (onChanged) onChanged(res.data);
        return;
      }
      alert(errorMessage(res, "Could not take the fee from the deposit."));
    } catch (err) {
      alert("Could not take the fee from the deposit.");
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    const note = window.prompt("Cancel this inspection? Optional note to the customer:");
    if (note === null) return;
    update({ status: "Cancelled", statusNote: note, notify: true }, "inspection");
  };

  const acceptRequest = (e) => {
    e.preventDefault();
    update({ status: "Accepted", statusNote: accept.statusNote.trim(), notify: accept.notify }, "request");
  };

  const declineRequest = () => {
    const note = window.prompt("Decline this request? Note to the customer (optional):");
    if (note === null) return;
    update({ status: "Cancelled", statusNote: note, notify: true }, "request");
  };

  const saveChecks = () => update({
    checks: checks
      .filter((c) => c._id || c.text.trim())
      .map((c) => ({ ...(c._id ? { _id: c._id } : {}), text: c.text.trim(), finding: c.finding })),
  }, "checklist");

  const addCheck = () => {
    const text = newCheck.trim();
    if (!text) return;
    setChecks((list) => [...list, { text, by: "staff", finding: "" }]);
    setNewCheck("");
  };

  const saveDecision = () => {
    if (!decision.value) {
      alert("Pick what the customer decided.");
      return;
    }
    update({ decision: { value: decision.value, reason: decision.reason.trim() } }, "decision");
  };

  // The account fields the API adds (withPurchaseIds), from an account summary.
  const withAccount = (i, account) => ({
    ...i,
    accountUserId: account._id,
    accountIsGuest: !!account.isGuest,
    depositBalance: account.depositBalance || 0,
  });

  // A guest account opened: only the account changes, so unsaved edits stay.
  const accountOpened = (account) => {
    setInspection((i) => withAccount(i, account));
    if (onChanged) onChanged(inspection);
  };

  // Money received can accept a waiting request; then the whole inspection
  // has moved on and is loaded again.
  const depositChanged = (depositRequest, data = {}) => {
    if (data.status && data.status !== inspection.status) {
      load();
    } else {
      setInspection((i) => (data.account ? withAccount({ ...i, depositRequest }, data.account) : { ...i, depositRequest }));
    }
    if (onChanged) onChanged({ ...inspection, depositRequest });
  };

  // A sale needs an account: a customer without one gets a guest account first.
  const recordPurchase = async () => {
    if (customerIdOf(inspection) || inspection.accountUserId) {
      onRecordPurchase(inspection, thread);
      return;
    }
    setBusy(true);
    const res = await openDealerGuestAccount({ inspectionId: inspection._id }).catch(() => null);
    setBusy(false);
    if (res?.data && res.data.account) {
      const next = withAccount(inspection, res.data.account);
      setInspection(next);
      onRecordPurchase(next, thread);
    } else {
      alert(errorMessage(res, "Could not open a guest account for the purchase.") + " Open it under Account with a corrected phone number.");
    }
  };

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const res = await uploadDealerInspectionMedia(inspectionId, files);
      if (res?.message === "Media uploaded") {
        setInspection((i) => ({ ...i, images: res.data.images, videos: res.data.videos }));
      } else {
        alert(errorMessage(res, "The upload failed."));
      }
    } catch (err) {
      alert("The upload failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const removeMedia = async (url) => {
    if (!window.confirm("Remove this from the report? The file is deleted for good.")) return;
    const res = await removeDealerInspectionMedia(inspectionId, url).catch(() => null);
    if (res?.message === "Media removed") {
      setInspection((i) => ({ ...i, images: res.data.images, videos: res.data.videos }));
      if (res.fileDeleted === false) alert("Removed from the report, but the file could not be deleted from storage, so its link still works.");
    } else {
      alert(errorMessage(res, "Could not remove it."));
    }
  };

  const replyBox = () => threadRef.current && threadRef.current.querySelector("textarea");
  // Photos picked for the reply and not sent yet.
  const replyFiles = () => !!(threadRef.current && threadRef.current.querySelector("[data-pending-files]"));

  const openReply = () => {
    setShowThread(true);
    // The box exists once the thread has rendered.
    setTimeout(() => { const box = replyBox(); if (box) box.focus(); }, 0);
  };

  if (!inspection) {
    return (
      <PanelFrame label="Inspection" onClose={onClose} header={<h2>Loading...</h2>} />
    );
  }

  const notesDirty = notes !== null && notes !== (inspection.adminNotes || "");
  const savedReport = inspection.report || {};
  const reportDirty = report.summary !== (savedReport.summary || "") || report.grade !== (savedReport.grade || "");
  const checksKey = (list) => JSON.stringify(list.map((c) => [c._id || "", c.text, c.finding || ""]));
  const checksDirty = checksKey(checks) !== checksKey(inspection.checks || []) || !!newCheck.trim();

  // Closing (×, the backdrop, Escape) asks first when something typed would be lost.
  const canClose = () => {
    const draft = replyBox();
    const unsaved = [
      notesDirty && "staff notes",
      reportDirty && "the report",
      checksDirty && "the checklist",
      ((draft && draft.value.trim()) || replyFiles()) && "your reply",
    ].filter(Boolean);
    return !unsaved.length || window.confirm(`Close without saving ${unsaved.join(" and ")}?`);
  };

  const decided = inspection.decision && inspection.decision.value;
  const isOpen = ["Requested", "Accepted", "Scheduled"].includes(inspection.status);
  const depositRequest = inspection.depositRequest || {};
  // Deposits usually run about 10% of the car.
  const v = inspection.vehicle || {};
  const carUsd = Number(v.source === "current-inventory" ? v.price : v.usdPrice) || 0;
  const suggestedDeposit = carUsd ? Math.round(carUsd * 0.1) : 0;
  // Their own account, one they opened since requesting as a guest, or a guest
  // account staff opened for them (no emails go to that one).
  const hasAccount = !!(customerIdOf(inspection) || inspection.accountUserId);
  const account = hasAccount ? { _id: inspection.accountUserId || customerIdOf(inspection), isGuest: !!inspection.accountIsGuest } : null;
  const guestAccount = !!(account && account.isGuest);
  const feeTaken = !!(inspection.feeCharged && inspection.feeCharged.at);
  const u = inspection.userId && typeof inspection.userId === "object" ? inspection.userId : null;
  const name = customerName(inspection);
  const email = (u && u.email) || inspection.email;
  const phone = inspection.phone || (u && u.phone) || "";
  // They didn't buy: the fee comes off their deposit before the refund.
  const feeDue = inspection.status === "Completed" && hasAccount && decided !== "buy" && !inspection.purchaseId
    && !feeTaken && inspection.feeAmount > 0;
  const canTakeFee = inspection.depositBalance >= inspection.feeAmount;
  // The decision form: the stage's own once the report is done; before that
  // when they decide not to wait (opened from More, or already recorded).
  const showDecision = inspection.status === "Completed" || (isOpen && (deciding || !!decided));

  const openDecision = () => {
    setDeciding(true);
    focusField(decisionField);
  };

  const changeDate = () => {
    setChangingDate(true);
    focusField(dateField);
  };

  const cancelDateChange = () => {
    setChangingDate(false);
    setSchedule((s) => ({ ...s, scheduledFor: inspection.scheduledFor || "" }));
  };

  // The next move, by stage. Each one but the sale takes staff to its form,
  // where the form's own button sends what it sends.
  let primary = null;
  if (inspection.status === "Requested") primary = { label: "Accept the request", onClick: () => focusField(acceptField) };
  else if (inspection.status === "Accepted") primary = { label: "Book a date", onClick: () => focusField(dateField) };
  else if (inspection.status === "Scheduled") primary = { label: "Write the report", onClick: () => focusField(summaryField) };
  if (decided === "buy" && !inspection.purchaseId && onRecordPurchase) primary = { label: "Start the sale", onClick: recordPurchase };
  else if (inspection.status === "Completed" && !decided) primary = { label: "Record their decision", onClick: () => focusField(decisionField) };

  const header = (
    <>
      <div className={styles.headerTop}>
        <h2>
          Inspection:{" "}
          {inspection.vehicle && inspection.vehicle.url
            ? <a href={inspection.vehicle.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>{vehicleLabel(inspection)}</a>
            : vehicleLabel(inspection)}
        </h2>
      </div>
      <div className={styles.hint}>
        {/* A request sent as a guest, by someone who has an account under that
            email, is theirs. Their page takes this panel's place, so ask about
            unsaved text first. */}
        <CustomerLink customer={customerRefOf(inspection, "inspection")} onOpen={onOpenCustomer && ((ref) => canClose() && onOpenCustomer(ref))}>
          {name}
        </CustomerLink>
        {email && email !== name ? ` · ${email}` : ""}
        {phone && <> · <a href={`tel:${phone}`} className={classes.clickableCell}>{formatPhone(phone)}</a></>}
      </div>
      <div className={styles.headerChips}>
        <span className={`${styles.chip} ${STATUS_TONE[inspection.status] || ""}`}>{inspection.status}</span>
        {hasAccount && (
          <span className={`${styles.chip} ${inspection.depositBalance > 0 ? styles.chipGood : styles.chipWarn}`}>
            {inspection.depositBalance > 0 ? `Deposit on file ${formatUsd(inspection.depositBalance, { cents: false })}` : "No deposit on file"}
          </span>
        )}
        <span className={`${styles.chip} ${styles.chipMuted}`}>{account ? (account.isGuest ? "Guest account" : "Account") : "No account"}</span>
        <span className={styles.hint}>
          Requested {formatDate(inspection.createdAt)} · {inspection.origin === "portal" ? "Portal" : "Website"}
        </span>
      </div>
      {inspection.status !== "Cancelled" && (
        <StepProgress plan={STAGES} status={stageOf(inspection)} label="Inspection stages" className={styles.progressCompact} />
      )}
      <div className={styles.headerActions}>
        {primary && (
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={primary.onClick}>
            {primary.label}
          </button>
        )}
        {inspection.status === "Scheduled" && !changingDate && (
          <button type="button" className={styles.btn} disabled={busy} onClick={changeDate}>Change the date</button>
        )}
        <MoreMenu
          disabled={busy}
          items={[
            feeDue && canTakeFee && { label: `Take the ${formatUsd(inspection.feeAmount, { cents: false })} fee from their deposit`, onClick: chargeFee },
            isOpen && !decided && !deciding && { label: "Record a decision now", title: "They're buying (or passing) without waiting for the inspection", onClick: openDecision },
            inspection.status === "Requested" && { label: "Decline the request", onClick: declineRequest },
            (inspection.status === "Accepted" || inspection.status === "Scheduled") && { label: "Cancel the inspection", onClick: cancel },
            email && { label: "Email in your mail app", href: `mailto:${email}?subject=${encodeURIComponent(`Re: ${vehicleLabel(inspection)}`)}` },
          ]}
        />
      </div>
    </>
  );

  const att = inspectionAttention(inspection, thread);
  // " · 3d" once it's been a day or more.
  const waited = (since) => { const age = ageLabel(since); return age && age !== "today" ? ` · ${age}` : ""; };
  let attention = null;
  if (att && att.kind === "yourTurn") {
    attention = (
      <div className={`${styles.attention} ${hoursSince(att.since) > REPLY_ALERT_HOURS ? styles.attentionBad : styles.attentionWarn}`}>
        <span className={styles.attentionText}>Customer replied - your turn · {ageLabel(att.since)}</span>
        <button type="button" className={styles.btn} onClick={openReply}>Reply</button>
      </div>
    );
  } else if (att && att.kind === "deposit") {
    attention = (
      <div className={`${styles.attention} ${styles.attentionWarn}`}>
        <span className={styles.attentionText}>
          Deposit{depositRequest.amount ? ` of ${formatUsd(depositRequest.amount, { cents: false })}` : ""} asked for {formatDate(att.since)} · not received yet{waited(att.since)}
        </span>
      </div>
    );
  } else if (att && att.kind === "dealer") {
    attention = (
      <div className={styles.attention}>
        <span className={styles.attentionText}>
          Waiting on the dealer in Japan since {formatDate(att.since)}
          {inspection.dealerAsk.note ? ` - ${inspection.dealerAsk.note}` : ""}
        </span>
      </div>
    );
  } else if (att && att.kind === "booked") {
    const days = daysUntil(att.since);
    let text = `Booked for ${formatDate(att.since)}`;
    if (days === 0) text = "Inspection day is today: write the report when it's in";
    else if (days === 1) text += " - tomorrow";
    else if (days > 1) text += ` - in ${days} days`;
    else if (days < 0) text += ` - ${-days} day${days === -1 ? "" : "s"} ago, the report is still to write`;
    attention = (
      <div className={`${styles.attention} ${days !== null && days <= 0 ? styles.attentionWarn : ""}`}>
        <span className={styles.attentionText}>{text}</span>
      </div>
    );
  } else if (att && att.kind === "deciding") {
    attention = (
      <div className={styles.attention}>
        <span className={styles.attentionText}>
          Report {guestAccount ? "done" : "sent"}{att.since ? ` ${formatDate(att.since)}` : ""} - waiting on their decision{waited(att.since)}
        </span>
      </div>
    );
  }

  const sidebar = (
    <>
      <div className={styles.sideCard}>
        <h3>Car</h3>
        <ul className={styles.carList}>
          <li>
            <VehicleThumb record={inspection} className={styles.cardThumb} />
            <div className={styles.carInfo}>
              {v.url
                ? <a href={v.url} target="_blank" rel="noopener noreferrer" className={classes.clickableCell}>{vehicleLabel(inspection)}</a>
                : <strong>{vehicleLabel(inspection)}</strong>}
              <div className={styles.hint}>
                {[
                  carUsd ? `listed ${formatUsd(carUsd, { cents: false })}` : "",
                  v.source === "current-inventory" ? "US inventory" : "Japan dealer stock",
                ].filter(Boolean).join(" · ")}
              </div>
            </div>
          </li>
        </ul>
      </div>

      {isOpen && (
        <div className={styles.sideCard}>
          <h3>Deposit</h3>
          <DepositRequestBox
            target={{ inspectionId: inspection._id }}
            request={depositRequest}
            account={account}
            depositBalance={account ? inspection.depositBalance : 0}
            suggestedAmount={suggestedDeposit}
            onChanged={depositChanged}
          />
        </div>
      )}

      {isOpen && (
        <div className={styles.sideCard}>
          <h3>Dealer in Japan</h3>
          <DealerWaitBox dealerAsk={inspection.dealerAsk} onSave={(body) => update(body, "dealer status")} />
        </div>
      )}

      {/* Never shown to the customer. */}
      <div className={styles.sideCard}>
        <h3>Staff notes</h3>
        <StaffNotesBox
          id="inspection-notes"
          rows={4}
          value={notes || ""}
          saved={inspection.adminNotes}
          onChange={setNotes}
          onSave={saveNotes}
          busy={busy}
        >
          {/* The lead's own notes, from before the inspection: its card is off
              the board now, so this is where they're read. */}
          {thread && thread.adminNotes && (
            <p className={styles.hint} style={{ whiteSpace: "pre-wrap" }}>
              <strong>From the lead:</strong> {thread.adminNotes}
            </p>
          )}
        </StaffNotesBox>
      </div>

      <div className={styles.sideCard}>
        <h3>Inspection fee</h3>
        <div className={styles.inlineRow}>
          {FEE_PRESETS.map((amount) => (
            <button
              key={amount}
              type="button"
              className={`${styles.btn} ${inspection.feeAmount === amount ? styles.btnPrimary : ""}`}
              disabled={busy || feeTaken}
              onClick={() => setFee(amount)}
            >
              {formatUsd(amount, { cents: false })}
            </button>
          ))}
          <form className={styles.inlineRow} style={{ margin: 0 }} onSubmit={saveOtherFee}>
            <input
              className={classes.formInput}
              style={{ width: 110, marginTop: 0 }}
              placeholder={FEE_PRESETS.includes(inspection.feeAmount) ? "Other $" : formatUsd(inspection.feeAmount, { cents: false })}
              aria-label="Other fee"
              value={otherFee}
              disabled={busy || feeTaken}
              onChange={(e) => setOtherFee(e.target.value)}
            />
            {otherFee && <button type="submit" className={styles.btn} disabled={busy}>Set</button>}
          </form>
        </div>
        <p className={styles.hint}>Never invoiced on its own: it goes on the final car invoice when they buy. If they don't, take it from their deposit before refunding the rest.</p>
        {feeTaken ? (
          <p className={styles.hint}>Taken from their deposit on {formatDate(inspection.feeCharged.at)}: {formatUsd(inspection.feeCharged.amount)}.</p>
        ) : inspection.purchaseId ? (
          <p className={styles.hint}>
            {inspection.status === "Completed"
              ? "They bought it: the fee goes on the car's final invoice (\"Everything\" adds it)."
              : "They're buying before the inspection: \"Everything\" only adds the fee once the inspection is completed. To charge it anyway, add it as a line."}
          </p>
        ) : feeDue ? (
          <p className={styles.hint}>
            {canTakeFee
              ? `They didn't buy: take it under More (deposit on file ${formatUsd(inspection.depositBalance, { cents: false })}).`
              : `They didn't buy, but their deposit on file (${formatUsd(inspection.depositBalance, { cents: false })}) doesn't cover the fee.`}
          </p>
        ) : null}
      </div>

      {(!account || account.isGuest) && (
        <div className={styles.sideCard}>
          <h3>Account</h3>
          <GuestAccountBox target={{ inspectionId: inspection._id }} record={inspection} account={account} onOpened={accountOpened} />
        </div>
      )}

      <div className={styles.sideCard}>
        <h3>History</h3>
        <ul className={`${styles.history} ${styles.historyCompact}`}>
          {[...(inspection.statusHistory || [])].reverse().map((h, i) => (
            <li key={`${h.status}-${h.at}-${i}`}>
              <span>{formatDate(h.at)}</span>
              <span>
                <strong>{h.status}</strong>
                {h.note ? ` - ${h.note}` : ""}
                {h.by && h.by.firstName ? <span className={styles.hint}> · {h.by.firstName}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </>
  );

  // ---- the forms, placed by stage below ----

  const acceptForm = (
    <form className={styles.panelSection} onSubmit={acceptRequest}>
      <h3>New request</h3>
      <p className={styles.hint}>
        Talk it through with the customer first. When they're ready, ask for the deposit (beside this): once it's
        marked received, this request is accepted for you. You can also accept it yourself.
        {guestAccount
          ? " No email goes to a guest account."
          : " They're told their inspection is confirmed and a date is coming - the deposit is never mentioned to them."}
      </p>
      <div className={classes.formGroup}>
        <label className={classes.formLabel} htmlFor="inspection-accept-note">Note to the customer (optional)</label>
        <input id="inspection-accept-note" ref={acceptField} className={classes.formInput} value={accept.statusNote} placeholder="e.g. Great talking with you - we're lining up the inspector now." onChange={(e) => setAccept((a) => ({ ...a, statusNote: e.target.value }))} />
      </div>
      {!guestAccount && (
        <label className={styles.checkboxRow}>
          <input type="checkbox" checked={accept.notify} onChange={(e) => setAccept((a) => ({ ...a, notify: e.target.checked }))} />
          Email the customer that it's accepted
        </label>
      )}
      <div className={classes.formButtons} style={{ alignItems: "center" }}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>Accept request</button>
      </div>
    </form>
  );

  const bookingForm = (
    <form className={styles.panelSection} onSubmit={saveSchedule}>
      <h3>{inspection.status === "Accepted" ? "Book the inspection" : "Change the date"}</h3>
      <div className={styles.grid2}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="inspection-date">Inspection date</label>
          <input id="inspection-date" ref={dateField} className={classes.formInput} type="date" value={schedule.scheduledFor} onChange={(e) => setSchedule((s) => ({ ...s, scheduledFor: e.target.value }))} />
        </div>
      </div>
      {inspection.status === "Accepted" && (
        <>
          <div className={classes.formGroup}>
            <label className={classes.formLabel} htmlFor="inspection-book-note">Note to the customer (optional)</label>
            <input id="inspection-book-note" className={classes.formInput} value={schedule.statusNote} onChange={(e) => setSchedule((s) => ({ ...s, statusNote: e.target.value }))} />
          </div>
          {!guestAccount && (
            <label className={styles.checkboxRow}>
              <input type="checkbox" checked={schedule.notify} onChange={(e) => setSchedule((s) => ({ ...s, notify: e.target.checked }))} />
              Email the customer the date
            </label>
          )}
        </>
      )}
      <div className={classes.formButtons} style={{ alignItems: "center" }}>
        <button type="submit" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy}>
          {inspection.status === "Accepted" ? "Book it" : "Change date"}
        </button>
        {inspection.status === "Scheduled" && (
          <button type="button" className={styles.btnText} disabled={busy} onClick={cancelDateChange}>Keep the date</button>
        )}
      </div>
    </form>
  );

  const reportForm = (
    <div className={styles.panelSection}>
      <h3>Report</h3>
      <div className={styles.grid2}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="inspection-grade">Overall grade / verdict</label>
          <input id="inspection-grade" className={classes.formInput} value={report.grade} placeholder="e.g. 4.5, Excellent" onChange={(e) => setReport((r) => ({ ...r, grade: e.target.value }))} />
        </div>
      </div>
      <div className={classes.formGroup}>
        <label className={classes.formLabel} htmlFor="inspection-summary">Inspector's summary (the customer reads this)</label>
        <textarea id="inspection-summary" ref={summaryField} className={classes.formInput} rows={6} value={report.summary} onChange={(e) => setReport((r) => ({ ...r, summary: e.target.value }))} />
      </div>

      <div className={classes.formButtons} style={{ alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={saveReport}>Save report</button>
        <button type="button" className={styles.btn} disabled={uploading} onClick={() => fileInput.current && fileInput.current.click()}>
          {uploading ? "Uploading..." : "Upload photos / video"}
        </button>
        <input ref={fileInput} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => upload(e.target.files)} />
        {inspection.status === "Scheduled" && (
          <button type="button" className={styles.btn} disabled={busy} onClick={complete}>
            Mark completed &amp; send report
          </button>
        )}
      </div>

      {((inspection.images || []).length > 0 || (inspection.videos || []).length > 0) && (
        <div className={styles.gallery}>
          {(inspection.images || []).map((url) => (
            <div key={url} className={styles.galleryItem}>
              <a href={url} target="_blank" rel="noopener noreferrer"><img src={url} alt="Inspection" loading="lazy" /></a>
              <button type="button" onClick={() => removeMedia(url)}>Remove</button>
            </div>
          ))}
          {(inspection.videos || []).map((url) => (
            <div key={url} className={styles.galleryItem}>
              <video src={url} controls preload="metadata" />
              <button type="button" onClick={() => removeMedia(url)}>Remove</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const decisionForm = (
    <div className={styles.panelSection}>
      <h3>Customer's decision</h3>
      {decided ? (
        <p>
          <span className={`${styles.chip} ${decided === "buy" ? styles.chipGood : styles.chipMuted}`}>
            {decided === "buy" ? "Buying" : "Passed - keep searching"}
          </span>{" "}
          <span className={styles.hint}>
            {inspection.decision.by === "customer" ? "from their garage" : "recorded by staff"}
            {inspection.decision.at ? ` on ${formatDate(inspection.decision.at)}` : ""}
          </span>
          {inspection.decision.reason && <><br />"{inspection.decision.reason}"</>}
        </p>
      ) : inspection.status === "Completed" ? (
        <p className={styles.hint}>Waiting on the customer. If they told you by phone or email, record it here.</p>
      ) : (
        <p className={styles.hint}>Buying without waiting for the inspection? Record it here and start the sale. Cancel the booking if it won't happen.</p>
      )}
      <div className={styles.grid2}>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="inspection-decision">Decision</label>
          <select id="inspection-decision" ref={decisionField} className={classes.formInput} value={decision.value} onChange={(e) => setDecision((d) => ({ ...d, value: e.target.value }))}>
            <option value="">Not decided</option>
            <option value="buy">Buying</option>
            <option value="decline">Passed - keep searching</option>
          </select>
        </div>
        <div className={classes.formGroup}>
          <label className={classes.formLabel} htmlFor="inspection-decision-note">{decision.value === "decline" ? "What they want instead" : "Note"}</label>
          <input id="inspection-decision-note" className={classes.formInput} value={decision.reason} onChange={(e) => setDecision((d) => ({ ...d, reason: e.target.value }))} />
        </div>
      </div>
      <div className={classes.formButtons} style={{ alignItems: "center", flexWrap: "wrap" }}>
        <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={saveDecision}>Save decision</button>
        {isOpen && !decided && (
          <button type="button" className={styles.btnText} onClick={() => setDeciding(false)}>Not now</button>
        )}
        {inspection.purchaseId && <span className={styles.hint}>Sale started.</span>}
      </div>
    </div>
  );

  // The report outside its own stage: once it's done (to correct it or add
  // photos), or whenever something is already written.
  const reportWritten = !!(savedReport.summary || savedReport.grade || (inspection.images || []).length || (inspection.videos || []).length);

  return (
    <PanelFrame
      label={`Inspection: ${vehicleLabel(inspection)}`}
      onClose={onClose}
      canClose={canClose}
      header={header}
      attention={attention}
      sidebar={sidebar}
    >
      {/* ---- The current stage's form ---- */}
      {inspection.status === "Requested" && acceptForm}
      {(inspection.status === "Accepted" || (inspection.status === "Scheduled" && changingDate)) && bookingForm}
      {inspection.status === "Scheduled" && reportForm}
      {showDecision && decisionForm}

      {/* ---- What the customer wants checked ---- */}
      {inspection.status !== "Cancelled" && (
        <div className={styles.panelSection}>
          <h3>What to check</h3>
          {checks.length === 0 && <p className={styles.hint}>The customer hasn't asked for anything specific.</p>}
          <ul className={styles.checkList}>
            {checks.map((c, i) => (
              // New staff items have no id until saved; the position is their identity.
              <li key={c._id || `new-${i}`}>
                <div className={styles.checkHead}>
                  <strong>{c.text}</strong>
                  <span className={styles.hint}>{c.by === "staff" ? "added by staff" : "from the customer"}</span>
                  {c.by === "staff" && (
                    <button
                      type="button"
                      className={styles.iconButton}
                      aria-label={`Remove ${c.text}`}
                      title="Remove"
                      onClick={() => setChecks((list) => list.filter((_, idx) => idx !== i))}
                    >
                      ×
                    </button>
                  )}
                </div>
                <textarea
                  className={classes.formInput}
                  rows={2}
                  value={c.finding}
                  placeholder="Finding - the customer sees this with the report"
                  aria-label={`Finding for ${c.text}`}
                  onChange={(e) => setChecks((list) => list.map((x, idx) => (idx === i ? { ...x, finding: e.target.value } : x)))}
                />
              </li>
            ))}
          </ul>
          <div className={classes.formButtons} style={{ alignItems: "center", flexWrap: "wrap" }}>
            <input
              className={classes.formInput}
              style={{ flex: "1 1 180px", minWidth: 0, marginTop: 0 }}
              value={newCheck}
              placeholder="Add something to check"
              aria-label="Add something to check"
              onChange={(e) => setNewCheck(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCheck(); } }}
            />
            <button type="button" className={styles.btn} onClick={addCheck} disabled={!newCheck.trim()}>Add</button>
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} disabled={busy} onClick={saveChecks}>Save checklist</button>
          </div>
        </div>
      )}

      {inspection.status !== "Scheduled" && (inspection.status === "Completed" || reportWritten) && reportForm}

      {/* ---- Conversation with the customer ---- */}
      {thread && (
        <div className={styles.panelSection} ref={threadRef}>
          <div className={styles.inlineRow} style={{ justifyContent: "space-between", margin: 0 }}>
            <h3 style={{ margin: 0 }}>Conversation</h3>
            <button type="button" className={styles.btnText} onClick={() => setShowThread((s) => !s)} aria-expanded={showThread}>
              {showThread ? "Hide" : `Show (${(thread.messages || []).length + (thread.message ? 1 : 0)} messages)`}
            </button>
          </div>
          {showThread && (
            <InquiryThread
              inquiry={thread}
              viewer="staff"
              sending={replying}
              onSendReply={sendReply}
              replyPlaceholder={thread.userId
                ? "Reply - the customer is emailed a link to their garage…"
                : "Reply - this guest receives the full text by email…"}
            />
          )}
        </div>
      )}
    </PanelFrame>
  );
};

export default InspectionPanel;
